import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Camera, Upload, Trash2, X, ImageIcon } from "lucide-react";
import api from "@/lib/api";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const MAX_PHOTOS = 5;

export function PhotoUploader({ reportId, section, photos, onPhotosChange, readOnly }) {
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const sectionPhotos = (photos || []).filter((p) => p.section === section);
  const canAdd = !readOnly && sectionPhotos.length < MAX_PHOTOS;

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large (max 10MB)");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("section", section);
      formData.append("caption", caption);
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/api/reports/${reportId}/photos`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Upload failed");
      }
      const photo = await res.json();
      onPhotosChange([...photos, photo]);
      setCaption("");
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err.message || "Failed to upload photo");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
      if (cameraRef.current) cameraRef.current.value = "";
    }
  };

  const handleDelete = async (photoId) => {
    try {
      await api.delete(`/reports/${reportId}/photos/${photoId}`);
      onPhotosChange(photos.filter((p) => p.id !== photoId));
      toast.success("Photo deleted");
    } catch {
      toast.error("Failed to delete photo");
    }
  };

  return (
    <div data-testid={`photo-uploader-${section}`}>
      {/* Photo grid */}
      {sectionPhotos.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
          {sectionPhotos.map((photo) => (
            <Card key={photo.id} className="border-border bg-background overflow-hidden group relative" data-testid={`photo-${photo.id}`}>
              <div className="aspect-square relative">
                <img
                  src={`${API_BASE}/api/uploads/${photo.filename}`}
                  alt={photo.caption || photo.original_name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                {!readOnly && (
                  <button
                    onClick={() => handleDelete(photo.id)}
                    className="absolute top-1.5 right-1.5 p-1 rounded-full bg-black/70 text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`delete-photo-${photo.id}`}
                  >
                    <X className="h-3.5 w-3.5" strokeWidth={2} />
                  </button>
                )}
              </div>
              {photo.caption && (
                <p className="text-xs text-muted-foreground px-2 py-1.5 truncate">{photo.caption}</p>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Upload controls */}
      {canAdd && (
        <div className="space-y-3">
          <div className="space-y-2">
            <Input
              data-testid={`photo-caption-${section}`}
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Optional caption..."
              className="bg-background border-border text-sm"
            />
          </div>
          <div className="flex gap-2">
            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => handleUpload(e.target.files?.[0])}
            />
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
              className="hidden"
              onChange={(e) => handleUpload(e.target.files?.[0])}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => cameraRef.current?.click()}
              disabled={uploading}
              data-testid={`camera-btn-${section}`}
              className="border-border text-xs flex-1"
            >
              <Camera className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
              {uploading ? "Uploading..." : "Take Photo"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid={`upload-btn-${section}`}
              className="border-border text-xs flex-1"
            >
              <Upload className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
              {uploading ? "Uploading..." : "Upload"}
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground text-center">
            {sectionPhotos.length}/{MAX_PHOTOS} photos
          </p>
        </div>
      )}

      {/* Empty state */}
      {sectionPhotos.length === 0 && readOnly && (
        <div className="flex items-center justify-center gap-2 py-4 text-muted-foreground text-sm">
          <ImageIcon className="h-4 w-4" strokeWidth={1.5} />
          No photos
        </div>
      )}
    </div>
  );
}
